import { useEffect, useRef } from "react";

import handleTypeWellMissingNormalizationToast, {
  TOAST_TRIGGERS
} from "components/arps/utils/handleTypeWellMissingNormalizationToast";

interface UseTypeWellNormalizationToastsProps {
  id: string;
  numberOfTypeWellsMissingNormalization: number;
  normalizeBy?: {
    active?: boolean;
    properties?: { field?: string; display?: string; displayName?: string };
  };
  useNormalizeBy: boolean;
  checkedGlobalTypeWells?: Array<{ id: string }>;
  hasTypeWellSaveSucceeded: boolean;
}

export const useTypeWellNormalizationToasts = ({
  id,
  numberOfTypeWellsMissingNormalization,
  normalizeBy,
  useNormalizeBy,
  checkedGlobalTypeWells,
  hasTypeWellSaveSucceeded
}: UseTypeWellNormalizationToastsProps) => {
  // Track user actions that should trigger the toast
  const previousCheckedTypeWells = useRef<string[] | null>(null);
  const previousNormalizationState = useRef<boolean | null>(null);
  const previousNormalizationField = useRef<string | null>(null);
  const previousMissingCount = useRef(0);
  const lastHandledSaveRef = useRef<boolean>(false);
  const missingCountAtSaveRef = useRef<number>(0);

  const isNormalizationActive = normalizeBy?.active || useNormalizeBy;
  const currentTypeWellIds = checkedGlobalTypeWells?.map((tw) => tw.id) || [];
  const currentField = normalizeBy?.properties?.field || "";

  // Main effect: show toast when missing count increases while normalization is active
  // Simple rule: if more type wells are now missing normalization data, notify the user
  useEffect(() => {
    // Skip first render - just initialize refs
    if (previousNormalizationState.current === null) {
      previousNormalizationState.current = isNormalizationActive;
      previousMissingCount.current = numberOfTypeWellsMissingNormalization;
      previousCheckedTypeWells.current = currentTypeWellIds;
      previousNormalizationField.current = currentField;
      return;
    }

    // Detect what changed for determining toast trigger type
    const normalizationJustEnabled =
      !previousNormalizationState.current && isNormalizationActive;

    const prevTypeWells = previousCheckedTypeWells.current ?? [];
    const hasNewTypeWellSelections = currentTypeWellIds.some(
      (twId) => !prevTypeWells.includes(twId)
    );

    const fieldChanged =
      previousNormalizationField.current !== "" &&
      previousNormalizationField.current !== currentField &&
      currentField !== "";

    // Show toast if missing count increased and normalization is active
    // This handles all cases: normalization toggled, type wells selected, field changed, or async data loaded
    const countIncreased =
      numberOfTypeWellsMissingNormalization > previousMissingCount.current;
    const shouldShowToast = countIncreased && isNormalizationActive;

    if (shouldShowToast) {
      // Determine trigger type for toast message
      const trigger =
        normalizationJustEnabled || fieldChanged
          ? TOAST_TRIGGERS.NORMALIZE
          : hasNewTypeWellSelections
          ? TOAST_TRIGGERS.SELECT
          : TOAST_TRIGGERS.NORMALIZE;

      handleTypeWellMissingNormalizationToast(
        id,
        numberOfTypeWellsMissingNormalization,
        trigger
      );
    }

    // Update refs
    previousNormalizationState.current = isNormalizationActive;
    previousMissingCount.current = numberOfTypeWellsMissingNormalization;
    previousCheckedTypeWells.current = currentTypeWellIds;
    previousNormalizationField.current = currentField;
  }, [
    id,
    numberOfTypeWellsMissingNormalization,
    isNormalizationActive,
    currentTypeWellIds,
    currentField
  ]);

  // handle type well save success toast
  useEffect(() => {
    if (
      hasTypeWellSaveSucceeded &&
      !lastHandledSaveRef.current &&
      isNormalizationActive &&
      checkedGlobalTypeWells?.length > 0
    ) {
      // use the captured count from when save was initiated, not current count
      if (missingCountAtSaveRef.current > 0) {
        handleTypeWellMissingNormalizationToast(
          id,
          missingCountAtSaveRef.current,
          TOAST_TRIGGERS.SAVE
        );
      }
      lastHandledSaveRef.current = true;
    } else if (!hasTypeWellSaveSucceeded) {
      lastHandledSaveRef.current = false;
      missingCountAtSaveRef.current = numberOfTypeWellsMissingNormalization;
    }
  }, [
    id,
    hasTypeWellSaveSucceeded,
    isNormalizationActive,
    checkedGlobalTypeWells,
    numberOfTypeWellsMissingNormalization
  ]);
};
